import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';

export interface Notification {
  id: string;
  userId: string;
  type: 'BOOKING_CREATED' | 'BOOKING_APPROVED' | 'BOOKING_REJECTED' | 'BOOKING_CANCELLED' | 'BOOKING_REMINDER' | 'PENDING_APPROVAL' | 'SYSTEM_ANNOUNCEMENT';
  title: string;
  message: string;
  link: string | null;
  bookingId: string | null;
  isRead: boolean;
  readAt: Date | null;
  createdAt: Date;
}

interface NotificationListResponse {
  success: boolean;
  data: Notification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

interface UnreadCountResponse {
  success: boolean;
  data: {
    count: number;
  };
}

/**
 * Hook to fetch user's notifications with pagination
 */
export const useNotifications = (params?: { isRead?: boolean; page?: number; limit?: number }) => {
  return useQuery<NotificationListResponse>({
    queryKey: ['notifications', params],
    queryFn: async () => {
      const { data } = await api.get('/notifications', { params });
      return data;
    },
    // Disable aggressive polling - only refetch on window focus or manual refresh
    refetchInterval: false,
    refetchOnWindowFocus: true,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
};

/**
 * Infinite-scroll hook — loads 10 notifications per page, auto-paginated.
 */
export const useInfiniteNotifications = () => {
  return useInfiniteQuery<NotificationListResponse>({
    queryKey: ['notifications', 'infinite'],
    queryFn: async ({ pageParam = 1 }) => {
      const { data } = await api.get('/notifications', {
        params: { page: pageParam, limit: 10 },
      });
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, pages } = lastPage.pagination;
      return page < pages ? page + 1 : undefined;
    },
    refetchInterval: false,
    refetchOnWindowFocus: true,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to get unread notification count
 */
export const useUnreadCount = () => {
  return useQuery<UnreadCountResponse>({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const { data } = await api.get('/notifications/unread-count');
      return data;
    },
    // Reduced from 15s to 2 minutes - bell badge updates on window focus or manual action
    refetchInterval: 2 * 60 * 1000, // Refetch every 2 minutes (much less aggressive)
    refetchOnWindowFocus: true,
    staleTime: 1 * 60 * 1000, // Consider data fresh for 1 minute
  });
};

/**
 * Hook to mark a notification as read
 */
export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { data } = await api.patch(`/notifications/${notificationId}/read`);
      return data;
    },
    onSuccess: () => {
      // Invalidate both notifications list and unread count
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

/**
 * Hook to mark all notifications as read
 */
export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.patch('/notifications/mark-all-read');
      return data;
    },
    onSuccess: () => {
      // Invalidate both notifications list and unread count
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

/**
 * Hook to delete a notification
 */
export const useDeleteNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { data } = await api.delete(`/notifications/${notificationId}`);
      return data;
    },
    onSuccess: () => {
      // Invalidate both notifications list and unread count
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};
